import { makeAutoObservable, observable, runInAction } from "mobx"
import sensorBatchStore from "../../sensor-batch/stores/sensor-batch-store"
import { returnOnCatch, returnOnFail, StoreResults, StoreResultsGeneric } from "../../../../stores/stores-helper"
import {
    AddStockAllocation,
    DeleteStockAllocation,
    DispatchEditListDto,
    EditStockAllocation,
} from "features/iot/stock-management/models/stock-management-models"
import { Sensor } from "features/iot/sensors/models/sensor-models"
import { getAllSensors } from "features/iot/sensors/services/sensor-services"
import customerStore from "features/organizations/customers/store/customer-store"
import { SBSensor } from "features/iot/sensor-batch/models/sensor-batch-models"
import { convertDDMMMYYYYtoDate } from "components/utils/date-formatter-utils"
import { addStockAllocation, deleteStockAllocation, editDispatches, editStockAllocation } from "../services/stock-management-service"

class StockManagementStore {
    stockManagementLoading: boolean = false
    sensorMap: Record<string, Sensor> = observable.object({})
    sensorLoading: boolean = false
    constructor() {
        makeAutoObservable(this)
    }

    loadAllSensors = async () => {
        try {
            if (this.sensorLoading) {
                return
            }

            runInAction(() => {
                this.sensorLoading = true
            })

            const data = (await getAllSensors()) || []
            runInAction(() => {
                data.forEach((sensor: Sensor) => {
                    this.sensorMap[sensor.macAddress] = sensor
                })

                this.sensorLoading = false
            })
        } catch (error) {
            console.error(`Failed to load sensors`, error)
        } finally {
            runInAction(() => {
                this.sensorLoading = false
            })
        }
    }

    get allSensorsStockManagement() {
        return Object.values(this.sensorMap)
    }

    getSensorSiteNameByMacAddress(macAddress: string): string | undefined {
        const sbSensor = sensorBatchStore.getSensorByMacAddress(macAddress)
        const siteId = sbSensor?.details?.siteId
        if (!siteId) return undefined
        return customerStore.siteMap[siteId].name
    }

    getSensorCustomerNameByMacAddress(macAddress: string): string | undefined {
        const sbSensor = sensorBatchStore.getSensorByMacAddress(macAddress)
        const customerId = sbSensor?.details?.customerId
        if (!customerId) return undefined
        return customerStore.customerMap[customerId].name
    }

    getStockSensorByMacAddress(macAddress: string): Sensor | null {
        const sensor = this.sensorMap[macAddress]
        if (!sensor) {
            return null
        }
        return sensor
    }

    getSensorByMacAddress(macAddress: string): Sensor | undefined {
        return this.sensorMap[macAddress]
    }

    addStockAllocation = async (entity: AddStockAllocation): Promise<StoreResultsGeneric<SBSensor>> => {
        try {
            this.onStart()
            const response = await addStockAllocation(entity as AddStockAllocation)

            if (response.statusCode === 200) {
                const result = response.result as SBSensor[]

                // Validate that each item in result has the necessary SBSensor properties
                if (!result.every(s => s.macAddress && s.serialNumber)) {
                    throw new Error("Invalid response format. Missing required properties in SBSensor.")
                }

                runInAction(() => {
                    result.forEach(sensor => {
                        this.updateSensorAndBatch(sensor)
                    })
                })

                return { passed: true, message: "" }
            } else {
                return returnOnFail()
            }
        } catch (error) {
            return returnOnCatch(error)
        } finally {
            this.onFinally()
        }
    }

    /**
     * Update both the sensor and the corresponding sensor batch.
     */
    private updateSensorAndBatch(sensor: SBSensor) {
        const sensorMacAddress = sensor.macAddress
        const existingSensor = sensorBatchStore.sensorsMap[sensorMacAddress]

        if (existingSensor) {
            const updatedSensor: SBSensor = {
                ...existingSensor,
                dispatched: sensor.dispatched,
                allocated: sensor.allocated,
                details: {
                    ...existingSensor.details,
                    allocationDate: sensor?.details?.allocationDate ?? existingSensor?.details?.allocationDate,
                    customerId: sensor?.details?.customerId ?? existingSensor?.details?.customerId,
                    siteId: sensor?.details?.siteId,
                    dispatchDate: sensor?.details?.dispatchDate ?? existingSensor?.details?.dispatchDate,
                    provisioningId: sensor?.details?.provisioningId ?? existingSensor?.details?.provisioningId,
                    provisioningDate: sensor?.details?.provisioningDate ?? existingSensor?.details?.provisioningDate,
                    provisioningDescription: sensor?.details?.provisioningDescription ?? existingSensor?.details?.provisioningDescription,
                },
            }

            // Update the sensorBatch with the updated sensor
            const sensorBatch = sensorBatchStore.getSensorBatchBySensorMacAddress(sensorMacAddress)
            if (sensorBatch) {
                const updatedBatchSensors = sensorBatch.sensors.map(s => (s.macAddress === sensorMacAddress ? updatedSensor : s))
                sensorBatchStore.sensorBatchMap[sensorBatch.id] = {
                    ...sensorBatch,
                    sensors: updatedBatchSensors,
                }
            }

            // Update the sensor map
            sensorBatchStore.sensorsMap[sensorMacAddress] = updatedSensor
        }
    }

    updateStockAllocation = async (entity: EditStockAllocation): Promise<StoreResults> => {
        try {
            this.onStart()

            const response = await editStockAllocation(entity)
            if (response.statusCode === 200) {
                runInAction(() => {
                    const macAddresses = entity?.listOfSensorsMacAddress || []
                    macAddresses.forEach(mac => {
                        const sbSensor = sensorBatchStore.getSensorByMacAddress(mac)
                        if (!sbSensor) return

                        // Ensure 'allocated' is properly set based on the allocation date
                        sbSensor.allocated = Boolean(entity?.newAllocationDate)

                        // Ensure 'details' object is initialized if undefined
                        sbSensor.details = sbSensor.details ?? {
                            customerId: undefined,
                            siteId: undefined,
                            allocationDate: undefined,
                            dispatchDate: undefined,
                            provisioningId: undefined,
                            provisioningDate: undefined,
                            provisioningDescription: undefined,
                        }

                        // Safely set details properties if entity is defined
                        sbSensor.details.allocationDate = convertDDMMMYYYYtoDate(entity?.newAllocationDate) ?? undefined
                        sbSensor.details.customerId = entity?.newCustomerId || sbSensor.details.customerId
                        sbSensor.details.siteId = entity?.newSiteId

                        // Update sensor and batch
                        this.updateSensorAndBatch(sbSensor)
                    })
                })

                return { passed: true, message: "" }
            } else {
                return returnOnFail()
            }
        } catch (error) {
            return returnOnCatch(error)
        } finally {
            this.onFinally()
        }
    }

    deleteStockAllocation = async (entity: DeleteStockAllocation): Promise<StoreResults> => {
        try {
            this.onStart()
            const response = await deleteStockAllocation(entity)
            if (response.statusCode === 200) {
                runInAction(() => {
                    const macAddresses = entity.listOfSensorsMacAddress
                    macAddresses.forEach(mac => {
                        const sbSensor = sensorBatchStore.getSensorByMacAddress(mac)
                        if (!sbSensor) return

                        sbSensor.allocated = false
                        sbSensor.dispatched = false
                        sbSensor.details = undefined
                        this.updateSensorAndBatch(sbSensor)
                    })
                })
                return { passed: true, message: "" }
            } else {
                return returnOnFail()
            }
        } catch (error) {
            return returnOnCatch(error)
        } finally {
            this.onFinally()
        }
    }

    updateDispatch = async (entity: DispatchEditListDto): Promise<StoreResults> => {
        try {
            this.onStart()

            const response = await editDispatches(entity)
            if (response.statusCode === 200) {
                runInAction(() => {
                    const macAddresses = entity.sensorsMacAddress
                    macAddresses.forEach(mac => {
                        const sbSensor = sensorBatchStore.getSensorByMacAddress(mac)
                        if (!sbSensor) return

                        sbSensor.dispatched = true

                        sbSensor.details = sbSensor.details ?? {
                            customerId: undefined,
                            siteId: undefined,
                            allocationDate: undefined,
                            dispatchDate: undefined,
                            provisioningId: undefined,
                            provisioningDate: undefined,
                            provisioningDescription: undefined,
                        }
                        sbSensor.details.dispatchDate = convertDDMMMYYYYtoDate(entity.dispatchDate) as Date

                        this.updateSensorAndBatch(sbSensor)
                    })
                })
                return { passed: true, message: "" }
            } else {
                return returnOnFail()
            }
        } catch (error) {
            return returnOnCatch(error)
        } finally {
            this.onFinally()
        }
    }

    private onStart() {
        runInAction(() => {
            sensorBatchStore.sensorBatchLoading = true
        })
    }

    private onFinally() {
        runInAction(() => {
            sensorBatchStore.sensorBatchLoading = false
        })
    }
}

export default new StockManagementStore()
